import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {NodeProps, useStore} from 'reactflow';

import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isStepSucceeded} from 'pipelines/PipelineStepStatusUtils';
import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {
  getExpectedStepDataKind,
  getInputStepDataShape,
  getOutputStepDataShape,
} from 'pipelines/StepDataUtils';
import {getIconForStepDataKind} from 'pipelines/StepIconUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import removeNulls from 'util/removeNulls';

import {AnalysisFlowchartAction} from './actions/AnalysisFlowchartActionTypes';
import {AnalysisGraphNodeData} from './analysis-graph/AnalysisGraphTypes';
import {AnalysisFlowchartMode, useAnalysisFlowchartContext} from './AnalysisFlowchartContext';
import StepDataHandle from './handles/StepDataHandle';
import useStepNodeContents from './node-contents/useStepNodeContents';
import StepPopover from './popovers/StepPopover';
type AnalysisFlowchartNodeProps = Pick<
  NodeProps<AnalysisGraphNodeData>,
  'data' | 'dragging' | 'id' | 'xPos' | 'yPos'
>;

/**
 * Get the input or output step datas listed on a step's node.
 *
 * Once a step has run, an interactive flowchart omits the optional outputs the run left empty, since there
 * is nothing to show or connect for them. Outputs connected to downstream steps are always listed, so their
 * edges keep a handle to attach to.
 */
export function getDisplayedStepDatas(
  step: PipelineStep,
  role: 'INPUT' | 'OUTPUT',
  mode: AnalysisFlowchartMode
): Array<PipelineStepData> {
  if (role === 'INPUT') {
    return step.inputStepDatas;
  }
  if (mode !== AnalysisFlowchartMode.INTERACTIVE || !isStepSucceeded(step.statusCode)) {
    return step.outputStepDatas;
  }
  return step.outputStepDatas.filter(
    (stepData) =>
      stepData.rawDatas.length > 0 ||
      getOutputStepDataShape(step.stepType, step.stepVersion, stepData.name).isRequired ||
      step.outgoingEdges.some(({fromStepData}) => fromStepData.name === stepData.name)
  );
}

/**
 * Check if the input or output step datas section should be hidden.
 *
 * An input or output step datas section is hidden if there is a single step data and the step only processes
 * data frames.
 */
export function hideStepDatasSection(
  step: PipelineStep,
  role: 'INPUT' | 'OUTPUT',
  mode: AnalysisFlowchartMode = AnalysisFlowchartMode.STATIC
): boolean {
  const stepDatas = getDisplayedStepDatas(step, role, mode);
  if (stepDatas.length === 0) {
    return true;
  }
  if (stepDatas.length > 1) {
    return false;
  }
  return [...step.inputStepDatas, ...getDisplayedStepDatas(step, 'OUTPUT', mode)].every(
    (stepData) => getExpectedStepDataKind(step, stepData) === PipelineStepDataKind.DATASET
  );
}

export default function AnalysisFlowchartNode({
  id: nodeId,
  data: {step},
  dragging,
}: AnalysisFlowchartNodeProps): JSX.Element {
  const isConnecting = useStore((state) => Boolean(state.connectionNodeId));
  const {mode, stepTypeInfos, getActionInfo, highlightedStepIds, selectedNodeIds, setSelectedNodeIds} =
    useAnalysisFlowchartContext();
  const isDimmed = highlightedStepIds.size > 0 && !highlightedStepIds.has(step.id);
  const isSelected = selectedNodeIds.has(nodeId);

  const {nodeName, nodeIcon, nodeStatus, nodeSecondaryInfo, isPreview} = useStepNodeContents(
    step,
    mode,
    stepTypeInfos
  );

  const outputStepDatas = getDisplayedStepDatas(step, 'OUTPUT', mode);
  const hideInputStepDatasSection = hideStepDatasSection(step, 'INPUT', mode);
  const hideOutputStepDatasSection = hideStepDatasSection(step, 'OUTPUT', mode);

  const sections = removeNulls([
    hideInputStepDatasSection
      ? null
      : {
          leftHeader: 'Inputs',
          sectionRows: step.inputStepDatas.map((stepData) => ({
            leftHandle: <StepDataHandle step={step} stepData={stepData} />,
            primaryText: getInputStepDataShape(step.stepType, step.stepVersion, stepData.name).displayName,
            secondaryText: '',
            icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
          })),
        },
    hideOutputStepDatasSection
      ? null
      : {
          leftHeader: 'Outputs',
          sectionRows: outputStepDatas.map((stepData) => ({
            rightHandle: <StepDataHandle step={step} stepData={stepData} />,
            primaryText: getOutputStepDataShape(step.stepType, step.stepVersion, stepData.name).displayName,
            secondaryText: '',
            icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
          })),
        },
  ]);

  return (
    <StepPopover step={step} disabled={isSelected || isConnecting || dragging}>
      <div
        className={css(styles.node, isDimmed && styles.dimmedNode)}
        data-dimmed={isDimmed}
        data-selected={isSelected}
        data-step-popover-disabled={isSelected || isConnecting || dragging}
        aria-current={highlightedStepIds.has(step.id) || undefined}
        {...getTestProps({component: 'AnalysisFlowchartNode', element: 'root', key: nodeId})}
        onClick={(e) => {
          // Reserve Shift+Click for multi-selecting nodes, versus opening step configuration.
          if (!(e.shiftKey || e.metaKey || e.ctrlKey)) {
            getActionInfo(AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION, step.id).doAction?.(step.id);
          }
        }}
        onContextMenu={(e) => {
          // Select the node on right-click, so `<AnalysisFlowchartToolbar>` appears for this node.
          setSelectedNodeIds(new Set([nodeId]));
          e.preventDefault();
        }}
        data-amp-safe={false}
      >
        <BenchlingNode.Standard.Node
          headerProps={{
            title: nodeName,
            leftIcon: nodeIcon,
            subTitle: nodeSecondaryInfo,
            leftHandle:
              hideInputStepDatasSection && step.inputStepDatas.length === 1 ? (
                <StepDataHandle step={step} stepData={step.inputStepDatas[0]} />
              ) : null,
            rightHandle:
              hideOutputStepDatasSection && outputStepDatas.length === 1 ? (
                <StepDataHandle step={step} stepData={outputStepDatas[0]} />
              ) : null,
          }}
          isSelected={isSelected}
          status={nodeStatus}
          isPreview={isPreview}
          sectionsProps={sections.length > 0 ? {sections} : undefined}
        />
      </div>
    </StepPopover>
  );
}

const styles = StyleSheet.create({
  node: {
    wordBreak: 'break-word', // `Float` applies `break-all`, which breaks in the middle of words
  },
  dimmedNode: {
    opacity: 0.3,
    transition: 'opacity 200ms ease-in-out',
  },
});
